import React from 'react';
import { Flex, Div, Gutter, Title, BodyText, Dropdown, GridBag, Image, Icon } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { UrlImage, LocalProperty, LocalPropertyValue, AdminProductVariant } from '@cdaas/api-clients/dist/product-service-openapi';

import { updateEntityByIndex, removeEntityByIndex } from '../../../../utils/entities';

import './Images.scss';

interface ImagesProps {
  title: string;
  images: UrlImage[] | undefined;
  variationsExist: boolean;
  variants: AdminProductVariant[] | undefined;
  allPropertiesInSystem: LocalProperty[];
  properties: LocalProperty[] | undefined;
  handleImagesChange: (images: UrlImage[]) => void;
}

function Images(props: ImagesProps) {
  const { title, images, variationsExist, variants, properties, allPropertiesInSystem, handleImagesChange } = props;
  const { t } = useTranslation('productscreen');

  const handleImageDeletion = (index: number) => {
    const updatedImages = removeEntityByIndex(images, index);
    handleImagesChange(updatedImages);
  };

  const checkVariantWithPropertyValueCombinationExists = (propertyValueCombinationToCheck: number[], variants: AdminProductVariant[]): boolean => {
    for (const variant of variants) {
      const productVariantPropertyValueIds = JSON.stringify((variant.property_value_ids || []).sort());
      const propertyValueCombination = JSON.stringify(propertyValueCombinationToCheck.sort());
      if (productVariantPropertyValueIds === propertyValueCombination) {
        return true;
      }
    }
    return false;
  };

  const checkVariantWithPropertyValueExists = (propertyValueToCheck: LocalPropertyValue, variants: AdminProductVariant[]): boolean => {
    for (const variant of variants) {
      if (variant.property_value_ids?.includes(propertyValueToCheck.id)) {
        return true;
      }
    }
    return false;
  };

  // Option with id -1 meaning image is applicable to all values of specific property
  const allOption = { id: -1, value: t('images_section.image_applicable_to_all') };

  const handleImagePropertyValueChange = (imageIndex: number, image: UrlImage, propertyId: number | undefined, propertyValueId: number) => {
    // Get property of the to-be-added propertyValue and all the possible values of that property
    // The properties data coming with product doesn't contain all the possible values for the properties.
    // allPropertiesInSystem does have the values also
    const property = allPropertiesInSystem.find(property => property.id === propertyId);
    const propertyValueIds = property?.property_values.map(propertyValue => propertyValue.id) || [];

    // If image has propertyValue that belongs to the same property as the to-be-added propertyValue, remove it before adding the new
    let updatedPropertyValueIds = (image.property_value_ids || []).filter(propertyValueId => !propertyValueIds.includes(propertyValueId));

    // In case image has no property value for a property (artificial allOption), it means the image is applicable to all possible values of that property
    // If the newly chosen property value is however not the allOption add it to property values of the image and check combination available
    if (propertyValueId !== allOption.id) {
      updatedPropertyValueIds.push(propertyValueId);
      // In case there is no product variant with the chosen combination of property values remove other choices except the now changed property value
      // E.g. if the old combination was [Blue, 64GB], requested combination is [Red, 64GB] but there is no variant like that -> set to [Red, All]
      if (!checkVariantWithPropertyValueCombinationExists(updatedPropertyValueIds, variants || [])) {
        updatedPropertyValueIds = [propertyValueId];
      }
    }

    const updatedImage = { ...image, property_value_ids: updatedPropertyValueIds };
    const updatedImages = updateEntityByIndex(images, imageIndex, updatedImage);
    handleImagesChange(updatedImages);
  };

  const propertyValuesOptions = (properties || []).map(property => {
    const propertyWithAllValues = allPropertiesInSystem.find(propertyWithAllValues => propertyWithAllValues.id === property.id);
    const propertyValues = propertyWithAllValues?.property_values?.slice() || [];
    const propertyValuesWithDisabledInfo = propertyValues.map(propertyValue => {
      return { ...propertyValue, disabled: !checkVariantWithPropertyValueExists(propertyValue, variants || []) };
    });
    propertyValuesWithDisabledInfo.unshift({ ...allOption, property_id: property.id, disabled: false });
    return propertyValuesWithDisabledInfo;
  });

  const displayableImages = (images || []).map(image => {
    const name = t('images_section.image_name_placeholder'); // TODO - not currently part of data model (should it be?)
    const selectedPropertyValues = propertyValuesOptions.map(valueOptionsForOnePropertyType => {
      const selectedValue = valueOptionsForOnePropertyType.find(valueOption => image.property_value_ids?.includes(valueOption.id));
      return selectedValue || { ...allOption, property_id: valueOptionsForOnePropertyType[0].property_id };
    });
    return { image, name, selectedPropertyValues, propertyValuesOptions };
  });

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('images_section.info')}</BodyText>
      <Gutter />
      <Div>
        {displayableImages.map((displayableImage, imageIndex) => (
          <Div key={imageIndex} data-testid={`image-${imageIndex}`} backgroundColor="gray100" margin={{ vertical: 'xs' }}>
            <GridBag key={imageIndex}>
              <GridBag.Item md={2}>
                <Image
                  style={{ height: '80px', width: '80px' }}
                  src={displayableImage?.image.small_size_url || ''}
                  alt={t('images_section.product_image_alt')}
                />
              </GridBag.Item>
              <GridBag.Item md={3}>
                <Flex centeredVertically>
                  <BodyText>{displayableImage.name}</BodyText>
                </Flex>
              </GridBag.Item>
              {variationsExist &&
                displayableImage.selectedPropertyValues.map((propertyValue, propertyValueIndex) => (
                  <GridBag.Item
                    md={2}
                    key={propertyValueIndex}
                    data-testid={`image-${imageIndex}-tablecell-property-${propertyValue.property_id}`}
                    margin={{ vertical: 'xs' }}
                  >
                    <Dropdown
                      className="product-images-listing__property-value-applicable-to-image-dropdown"
                      name={`image-${imageIndex}-property-${propertyValue.property_id}`}
                      onChange={(_, option) =>
                        handleImagePropertyValueChange(
                          imageIndex,
                          displayableImage.image,
                          (propertyValue as { property_id: number }).property_id,
                          Number(option.value),
                        )
                      }
                      selectedOption={{ label: propertyValue.value || '', value: propertyValue.id.toString() }}
                      options={displayableImage.propertyValuesOptions[propertyValueIndex].map(propertyValueOption => ({
                        label: propertyValueOption.value || '',
                        value: propertyValueOption.id.toString(),
                        disabled: propertyValueOption.disabled,
                      }))}
                      required={true}
                    />
                  </GridBag.Item>
                ))}
              <GridBag.Item md={1}>
                <Flex centeredVertically>
                  <Icon
                    name="close-circle"
                    data-testid={`image-${imageIndex}-delete`}
                    size="sm"
                    color="gray500"
                    margin={{ horizontal: 'xs' }}
                    title={t('images_section.delete_image')}
                    onClick={() => handleImageDeletion(imageIndex)}
                    clickableAppearance
                  />
                </Flex>
              </GridBag.Item>
            </GridBag>
          </Div>
        ))}
      </Div>
      <Gutter size="sm" />
      <Flex flexItem occupyHorizontalSpace={false}>
        <BodyText
          margin={{ right: 'md' }}
          clickableAppearance={true}
          color="purple500"
          onClick={() => alert('TODO - Uploading images not yet implemented')}
        >
          {`+ ${t('images_section.upload_image')}`}
        </BodyText>
      </Flex>
    </Flex>
  );
}

export default React.memo(Images);
